/**
 * The number of units an owner has of a listing is `publicData.quantity`. It is a fixed figure
 * set by the owner — it is not decremented by bookings, and the marketplace has no reliable
 * per-date availability yet. So nothing here, and nothing built on it, may be worded as
 * availability for chosen dates. It is total stock.
 *
 * Until now the figure only surfaced as the size of the Quantity dropdown, and only once
 * dates were picked. A customer, a search engine or an AI assistant reading the page could
 * not tell whether "Table 8'" meant one table or twelve. This is the single place the value
 * is read and validated, so the panel text, the header, the structured data and the quantity
 * selector all agree.
 */

export const DEFAULT_UNIT_COUNT = 1;

/**
 * Total units for a listing. Always a whole number >= 1.
 *
 * `quantity` arrives as a string from the listing form and has been seen as "", "0", "abc"
 * and undefined in production data. Anything that is not a whole number of at least one is
 * treated as a single unit — which is also what the quantity selector assumes, so the text
 * and the dropdown can never disagree. A single unit is shown, not hidden: "1 unit" is as
 * much a fact as "12 units", and leaving it out would make a reader guess.
 *
 * @param {Object} [publicData]
 * @returns {number} integer >= 1
 */
export const getListingUnitCount = publicData => {
  const raw = publicData?.quantity;
  if (raw == null || raw === '') return DEFAULT_UNIT_COUNT;
  const parsed = typeof raw === 'number' ? raw : parseInt(String(raw).trim(), 10);
  if (!Number.isInteger(parsed) || parsed < 1) return DEFAULT_UNIT_COUNT;
  return parsed;
};
